import { FieldKey } from '../../context/field-keys.js';
import { areaConvert, FALLBACK_SPACE_UOM } from './space-uom.js';
/** FE mixed-path coercion (`makeAmountWith`: `value || 0`) — non-numeric garbage adds as 0. */
const toNumber = v => Number(v) || 0;
function runUomAwareSum(group, ctx, deps) {
  const a = ctx.get(group.a);
  const aUom = ctx.get(group.aUom);
  const b = ctx.get(group.b);
  const bUom = ctx.get(group.bUom);
  if (a == null && aUom == null && b == null && bUom == null) {
    ctx.set(group.out, null);
    ctx.set(group.outUom, null);
  } else if (a == null && aUom == null && b != null && bUom != null) {
    ctx.set(group.out, b);
    ctx.set(group.outUom, bUom);
  } else if (b == null && bUom == null && a != null && aUom != null) {
    ctx.set(group.out, a);
    ctx.set(group.outUom, aUom);
  } else if (a != null && aUom != null && b != null && bUom != null) {
    if (aUom === bUom) {
      // Fast path: same UOM string — add directly, keep that UOM (FE `makeAmount` + `addition`).
      ctx.set(group.out, toNumber(a) + toNumber(b));
      ctx.set(group.outUom, aUom);
    } else {
      // Mixed path: convert BOTH addends to the country-default space UOM; total carries it.
      const defaultUom = deps?.defaultSpaceUom ?? FALLBACK_SPACE_UOM;
      const sum = areaConvert(toNumber(a), aUom, defaultUom) + areaConvert(toNumber(b), bUom, defaultUom);
      ctx.set(group.out, sum);
      ctx.set(group.outUom, defaultUom);
    }
  }
  // Any other (partial) state: no write — mirrors the binder's fall-through.
}
function uomAwareSumRule(id, formulaVersion, group) {
  return {
    id,
    inputs: [group.a, group.aUom, group.b, group.bUom],
    outputs: [group.out, group.outUom],
    formulaVersion,
    run: (ctx, deps) => runUomAwareSum(group, ctx, deps)
  };
}
/** total = direct ⊕ sublease (UOM-aware). v2.0.0: was UOM-blind `+value` coercion in 1.0.0. */
export function totalFromDirectSubleaseRule(triple, formulaVersion = '2.0.0') {
  return uomAwareSumRule(`${triple.idPrefix}.total-from-direct-sublease`, formulaVersion, {
    a: triple.direct,
    aUom: triple.directUom,
    b: triple.sublease,
    bUom: triple.subleaseUom,
    out: triple.total,
    outUom: triple.totalUom
  });
}
export const AVAILABLE_SPACE_TRIPLE = {
  idPrefix: 'property.available-space',
  direct: FieldKey.DirectAvailableSpace,
  directUom: FieldKey.DirectAvailableSpaceUom,
  sublease: FieldKey.SubleaseAvailableSpace,
  subleaseUom: FieldKey.SubleaseAvailableSpaceUom,
  total: FieldKey.TotalAvailableSpace,
  totalUom: FieldKey.TotalAvailableSpaceUom
};
export const VACANT_SPACE_TRIPLE = {
  idPrefix: 'property.vacant-space',
  direct: FieldKey.DirectVacantSpace,
  directUom: FieldKey.DirectVacantSpaceUom,
  sublease: FieldKey.SubleaseVacantSpace,
  subleaseUom: FieldKey.SubleaseVacantSpaceUom,
  total: FieldKey.TotalVacantSpace,
  totalUom: FieldKey.TotalVacantSpaceUom
};
export const NOT_VACANT_SPACE_TRIPLE = {
  idPrefix: 'property.not-vacant-space',
  direct: FieldKey.DirectNotVacantSpace,
  directUom: FieldKey.DirectNotVacantSpaceUom,
  sublease: FieldKey.SubleaseNotVacantSpace,
  subleaseUom: FieldKey.SubleaseNotVacantSpaceUom,
  total: FieldKey.TotalNotVacantSpace,
  totalUom: FieldKey.TotalNotVacantSpaceUom
};
/** 4th binder group the UOM-blind port lacked: vacant-available (FE `*_vacant_available_area`). */
export const VACANT_AVAILABLE_SPACE_TRIPLE = {
  idPrefix: 'property.vacant-available-space',
  direct: FieldKey.DirectVacantAvailableSpace,
  directUom: FieldKey.DirectVacantAvailableSpaceUom,
  sublease: FieldKey.SubleaseVacantAvailableSpace,
  subleaseUom: FieldKey.SubleaseVacantAvailableSpaceUom,
  total: FieldKey.TotalVacantAvailableSpace,
  totalUom: FieldKey.TotalVacantAvailableSpaceUom
};
/** direct + sublease totals for all four availability/vacancy metrics. */
export const propertyAvailVacTotalRules = [totalFromDirectSubleaseRule(AVAILABLE_SPACE_TRIPLE), totalFromDirectSubleaseRule(VACANT_SPACE_TRIPLE), totalFromDirectSubleaseRule(NOT_VACANT_SPACE_TRIPLE), totalFromDirectSubleaseRule(VACANT_AVAILABLE_SPACE_TRIPLE, '1.0.0') // new in T2.6
];
/**
 * Derived per-side vacancy breakdown (binder groups 5–6, `total-availability-space.service.ts`
 * L170–205): vacant = vacant-available ⊕ vacant-not-available, same UOM-aware sum, per side.
 * Their outputs (Direct/SubleaseVacantSpace + UOM) chain into the vacant-space total rule.
 */
export const directVacantFromBreakdownRule = uomAwareSumRule('property.direct-vacant-space.from-available-not-available', '1.0.0', {
  a: FieldKey.DirectVacantAvailableSpace,
  aUom: FieldKey.DirectVacantAvailableSpaceUom,
  b: FieldKey.DirectNotVacantSpace,
  bUom: FieldKey.DirectNotVacantSpaceUom,
  out: FieldKey.DirectVacantSpace,
  outUom: FieldKey.DirectVacantSpaceUom
});
export const subleaseVacantFromBreakdownRule = uomAwareSumRule('property.sublease-vacant-space.from-available-not-available', '1.0.0', {
  a: FieldKey.SubleaseVacantAvailableSpace,
  aUom: FieldKey.SubleaseVacantAvailableSpaceUom,
  b: FieldKey.SubleaseNotVacantSpace,
  bUom: FieldKey.SubleaseNotVacantSpaceUom,
  out: FieldKey.SubleaseVacantSpace,
  outUom: FieldKey.SubleaseVacantSpaceUom
});
export const propertyVacantBreakdownRules = [directVacantFromBreakdownRule, subleaseVacantFromBreakdownRule];
